import { useFormik } from 'formik'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import clsx from 'clsx'
import { QUERY_KEYS } from 'constants/constants'
import trackEvents from '../../../constants/track-events'
import {
  useAivyContext,
  useLanguageContext,
  useNotificationContext,
  usePartnerContext
} from '../../../context'
import { trackEvent } from '../../../helpers/analytics'
import { useMutationAndUpdateQuery } from '../../../hooks/use-mutation-and-update-query'
import Button from '../../tailwind/Button'
import Toggle from '../../tailwind/toggle'
import ReadOnlyFieldset from './read-only-fieldset'

const I18N_PREFIX = 'components.settings.result_reports.'

const Divider = () => <div className='h-px w-full bg-gray-200' />

// readOnly gilt in enforced ohne CUSTOM_REPORTS, wenn schon Einstellungen
// gespeichert sind. Sie bleiben aktiv und lesbar, der Inhalt liegt in einem
// disabled fieldset und Speichern fehlt. Erlaubt ist nur Auf Standard
// zurücksetzen.
const SettingsV1 = ({ readOnly }) => {
  const { t } = useTranslation()
  const { system } = useAivyContext()
  const { success, error, alert } = useNotificationContext()
  const { partner } = usePartnerContext()
  const { language } = useLanguageContext()

  const report_v1_settings = JSON.parse(
    partner.app_settings?.report_v1_settings ?? '{}'
  )

  const { mutate } = useMutationAndUpdateQuery({
    queryKey: QUERY_KEYS.partnerById(partner.id),
    mutation: 'updatePartner'
  })

  const dimensions_v1 = Object.entries(JSON.parse(system.dimensions.data)).map(
    ([key, value]) => ({ key, ...value })
  )

  const SORT_OPTIONS = [
    {
      text: t(I18N_PREFIX + 'sort.byImportance'),
      value: 'IMPORTANCE'
    },
    {
      text: t(I18N_PREFIX + 'sort.byCategory'),
      value: 'CATEGORY'
    }
  ]

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      score_visible: report_v1_settings.score_visible ?? true,
      interpretation_guide_visible:
        report_v1_settings.interpretation_guide_visible ?? true,
      sort_by: report_v1_settings.sort_by ?? 'IMPORTANCE',
      hidden_dimensions: report_v1_settings.hidden_dimensions ?? []
    },
    onSubmit: (
      {
        score_visible,
        interpretation_guide_visible,
        sort_by,
        hidden_dimensions
      },
      { setSubmitting }
    ) => {
      setSubmitting(true)
      trackEvent(trackEvents.UPDATE_RESULT_REPORTS_SETTINGS_V1)

      const app_settings = { ...(partner.app_settings || {}) }
      app_settings.report_v1_settings = JSON.stringify({
        score_visible,
        interpretation_guide_visible,
        sort_by,
        hidden_dimensions
      })

      const input = { id: partner.id, app_settings }
      const onSuccess = () => {
        success(t(I18N_PREFIX + 'success'))
        setSubmitting(false)
      }

      mutate({ input }, { onSuccess })
    }
  })

  // Standard heißt, es ist nichts gespeichert, wie bei einem Partner, der
  // nie gespeichert hat. Nicht '{}', das PDF v1 (aivy-pdf-reports) nimmt ein
  // leeres Objekt statt seiner Defaults und bricht an hidden_dimensions.
  const resetToDefault = () => {
    formik.setSubmitting(true)
    trackEvent(trackEvents.UPDATE_RESULT_REPORTS_SETTINGS_V1, { reset: true })

    const app_settings = {
      ...(partner.app_settings || {}),
      report_v1_settings: null
    }

    mutate(
      { input: { id: partner.id, app_settings } },
      {
        onSuccess: () => {
          success(t(I18N_PREFIX + 'success'))
          formik.setSubmitting(false)
        },
        onError: () => {
          error()
          formik.setSubmitting(false)
        }
      }
    )
  }

  // Ohne CUSTOM_REPORTS lassen sich eigene Einstellungen danach nicht neu
  // anlegen, deshalb mit Rückfrage
  const confirmReset = () =>
    alert(
      t('pricing.gates.reports_reset_confirm_title'),
      [t('pricing.gates.reports_reset_confirm_text')],
      [
        {
          onPress: resetToDefault,
          text: t('pricing.gates.reports_reset_action'),
          style: 'destructive'
        },
        { text: t('pricing.dialog.cancel_action'), style: 'cancel' }
      ]
    )

  return (
    <>
      <ReadOnlyFieldset readOnly={readOnly} className='flex flex-col gap-y-4'>
        <Toggle
          label={t(I18N_PREFIX + 'score.title')}
          description={t(I18N_PREFIX + 'score.description')}
          appearance={'with-left-label-and-description'}
          enabled={formik.values.score_visible}
          setEnabled={() =>
            formik.setFieldValue('score_visible', !formik.values.score_visible)
          }
        />
        <Divider />
        <Toggle
          label={t(I18N_PREFIX + 'interpretation_guide.title')}
          description={t(I18N_PREFIX + 'interpretation_guide.description')}
          appearance={'with-left-label-and-description'}
          enabled={formik.values.interpretation_guide_visible}
          setEnabled={() =>
            formik.setFieldValue(
              'interpretation_guide_visible',
              !formik.values.interpretation_guide_visible
            )
          }
        />
        <Divider />
        <div>
          <label
            htmlFor='location'
            className='block text-sm font-medium leading-6 text-gray-900'
          >
            {t(I18N_PREFIX + 'sort.title')}
          </label>
          <select
            id='location'
            name='location'
            className='mt-1 block w-full max-w-sm rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-blue-600 sm:text-sm sm:leading-6'
            defaultValue={
              SORT_OPTIONS.filter((o) => o.value === formik.values.sort_by)[0]
                .text
            }
            onChange={(event) => {
              const value = SORT_OPTIONS.filter(
                (o) => o.text === event.target.value
              )[0].value
              formik.setFieldValue('sort_by', value)
            }}
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.value}>{o.text}</option>
            ))}
          </select>
          <div className='mt-2' />
        </div>
        <Divider />
        <div>
          <h4 className='text-sm font-medium leading-6 text-gray-900'>
            {t(I18N_PREFIX + 'dimensions.title')}
          </h4>
          <p className='text-sm text-gray-500'>
            {t(I18N_PREFIX + 'dimensions_visible', {
              count:
                dimensions_v1.length - formik.values.hidden_dimensions.length
            })}
          </p>
          <div className='mt-4 flex flex-col gap-y-6'>
            {[
              {
                category: system.categories['1'],
                dimensions: dimensions_v1.filter(
                  ({ category }) => category === 1
                )
              },
              {
                category: system.categories['2'],
                dimensions: dimensions_v1.filter(
                  ({ category }) => category === 2
                )
              },
              {
                category: system.categories['3'],
                dimensions: dimensions_v1.filter(
                  ({ category }) => category === 3
                )
              }
            ].map(({ category, dimensions }, index) => (
              <div key={`category_${index}`}>
                <h5 className='text-sm text-gray-700'>
                  {category.name[language] + ':'}
                </h5>
                <div className='mt-2 flex flex-row flex-wrap gap-2'>
                  {dimensions.map(({ key, property }, index) => {
                    const isHidden =
                      formik.values.hidden_dimensions.includes(key)

                    return (
                      <div
                        key={index}
                        onClick={() => {
                          if (readOnly) return

                          if (isHidden) {
                            formik.setFieldValue('hidden_dimensions', [
                              ...formik.values.hidden_dimensions.filter(
                                (dimension) => dimension !== key
                              )
                            ])
                          } else {
                            formik.setFieldValue('hidden_dimensions', [
                              ...formik.values.hidden_dimensions,
                              key
                            ])
                          }
                        }}
                        className={clsx(
                          'flex items-center justify-center rounded-full px-4 py-2 text-sm text-white',
                          readOnly ? 'cursor-default' : 'cursor-pointer',
                          isHidden
                            ? clsx(
                                'bg-blue-600/30',
                                !readOnly && 'hover:bg-blue-600/70'
                              )
                            : 'bg-blue-600'
                        )}
                      >
                        {property[language]}
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </ReadOnlyFieldset>
      <div className='mt-6 flex justify-end'>
        {readOnly ? (
          <Button.SecondaryXL
            text={t('pricing.gates.reports_reset_action')}
            onClick={confirmReset}
            isLoading={formik.isSubmitting}
          />
        ) : (
          <Button.PrimaryXL
            text={t(I18N_PREFIX + 'save_action')}
            className='bg-green-600 hover:bg-green-600/90'
            onClick={formik.handleSubmit}
            disabled={!formik.dirty}
            isLoading={formik.isSubmitting}
          />
        )}
      </div>
    </>
  )
}

SettingsV1.propTypes = { readOnly: PropTypes.bool }

SettingsV1.defaultProps = { readOnly: false }

export default SettingsV1
